/** NextGen P2 — costing method UI helpers (no network). */

export const COSTING_METHODS = [
  'RESOURCE_BUILDUP',
  'ASSEMBLY',
  'CREW_BASED',
  'ITEM_RATE',
  'MANUAL_RATE',
] as const;

export type CostingMethodCode = (typeof COSTING_METHODS)[number];
export type MethodTransitionMode = 'REPLACE' | 'PRESERVE';

export const COSTING_METHOD_LABELS: Record<CostingMethodCode, string> = {
  RESOURCE_BUILDUP: 'Resource build-up',
  ASSEMBLY: 'Assembly',
  CREW_BASED: 'Crew-based',
  ITEM_RATE: 'Item rate',
  MANUAL_RATE: 'Manual rate',
};

export function toConfirmBehavior(mode: MethodTransitionMode): 'replace' | 'preserve' {
  return mode === 'PRESERVE' ? 'preserve' : 'replace';
}

export function needsMethodConfirm(preview: {
  warning?: string | null;
  affectedCostLineCount?: number;
}): boolean {
  const n = Number(preview.affectedCostLineCount || 0);
  return !!(preview.warning || n > 0);
}

/** Unit direct cost from cost-line fields (mirrors backend effective unit before qty). */
export function computeUnitDirectCost(line: {
  rate?: unknown;
  waste_factor?: unknown;
  wasteFactor?: unknown;
  fx_rate?: unknown;
  fxRate?: unknown;
  productivity_factor?: unknown;
  productivityFactor?: unknown;
}): number | null {
  const rate = Number(line.rate);
  if (!Number.isFinite(rate)) return null;
  const waste = Number(line.waste_factor ?? line.wasteFactor) || 0;
  const fx = Number(line.fx_rate ?? line.fxRate) || 1;
  const prod = Number(line.productivity_factor ?? line.productivityFactor) || 1;
  return Math.round(((rate * (1 + waste)) / (prod || 1)) * fx * 10000) / 10000;
}

/** Codes that look Phase-2 costing related for Health drawer tagging. */
export function isPhase2HealthCode(code?: string): boolean {
  if (!code) return false;
  const c = code.toUpperCase();
  return (
    c.includes('ASSEMBLY') ||
    c.includes('COSTING') ||
    c.includes('RATE_') ||
    c.includes('_RATE') ||
    c.includes('METHOD') ||
    c.includes('CREW') ||
    c.includes('PRODUCTIVITY') ||
    c.includes('FX_') ||
    c.includes('UOM')
  );
}
